import { Alert, Button, Cascader, Form, Input, Spin, Switch } from "antd";
import type { CustomerReceivingAddress } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { addressService, type CustomerReceivingAddressForm } from "@/api/addressService";
import { Breadcrumb, ZhMessageBox } from "@/components/common";
import { loadAreas, type AreaNode } from "@/runtime/areaData";

type AddressFields = CustomerReceivingAddressForm & { region: string[]; defaultFlag: boolean };

/** 地址表单支持地区级联、旧版回跳规则及新增/编辑回显。 */
export function AddressFormPage() {
    const [form] = Form.useForm<AddressFields>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = Number(searchParams.get("id") || 0);
    const [areas, setAreas] = useState<AreaNode[]>([]);
    const [loading, setLoading] = useState(Boolean(id));
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingPayload, setPendingPayload] = useState<CustomerReceivingAddressForm>();
    const pageName = id ? "修改地址" : "新增地址";
    /** 地区数据复用公共 areaLib 适配层，避免列表与表单重复维护数据源。 */
    useEffect(() => {
        void loadAreas().then(setAreas);
    }, []);
    /** 编辑态回显三级地区和默认地址开关。 */
    const load = async () => {
        if (!id) return;
        setLoading(true);
        setError(false);
        try {
            const data = (await addressService.getAddressInfoById(id)) as unknown as CustomerReceivingAddress;
            form.setFieldsValue({
                contacts: data.contacts,
                cellphone: data.cellphone,
                detailAddress: data.detailAddress,
                areaCode: data.areaCode,
                landline: data.landline,
                subLandline: data.subLandline,
                zipCode: data.zipCode,
                isDefault: data.isDefault,
                province: data.province,
                city: data.city,
                district: data.district,
                region: [data.province, data.city, data.district],
                defaultFlag: Boolean(data.isDefault)
            });
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    /** 新增地址后按旧页面来源返回，并把新地址 ID 带回下单流程。 */
    const back = (newAddressId?: number) => {
        const fromPage = searchParams.get("fromPage");
        if (newAddressId && fromPage) {
            const productId = searchParams.get("productId");
            if (fromPage === "productConsignmentSampleApply")
                window.open(
                    `${window.location.origin}/public/productConsignment?productId=${productId}&addressId=${newAddressId}`,
                    "_self"
                );
            else if (fromPage === "confirmOrder")
                window.open(
                    `${window.location.origin}/public/confirmOrder/${productId}?addressId=${newAddressId}`,
                    "_self"
                );
            else if (fromPage === "bargainOrderConfirm")
                navigate(`/orderCenter/myOrder/orderConfirm/${searchParams.get("orderId")}?addressId=${newAddressId}`);
            return;
        }
        navigate(-1);
    };
    /** 校验通过后拆分地区字段，再打开旧版确认框。 */
    const requestSave = async () => {
        try {
            const values = await form.validateFields();
            const [province, city, district] = values.region;
            const { region: _region, defaultFlag, ...rest } = values;
            setPendingPayload({
                ...rest,
                province,
                city,
                district,
                areaCode: rest.areaCode || undefined,
                landline: rest.landline || undefined,
                subLandline: rest.subLandline || undefined,
                isDefault: defaultFlag ? 1 : 0
            });
        } catch {
            /* 表单组件负责显示字段错误。 */
        }
    };
    /** 对应旧 confirmWrapper：确认后新增或编辑，并执行原页面来源回跳规则。 */
    const save = async () => {
        if (!pendingPayload) return;
        const payload = pendingPayload;
        setPendingPayload(undefined);
        setSaving(true);
        try {
            if (id) {
                await addressService.editCustomerAddress(id, payload);
                back();
            } else {
                const created = await addressService.addCustomerAddress(payload);
                back(created.id);
            }
        } finally {
            setSaving(false);
        }
    };
    if (loading) return <Spin fullscreen tip="地址加载中" />;
    if (error)
        return (
            <Alert
                type="error"
                showIcon
                message="地址加载失败"
                action={<Button onClick={() => void load()}>重试</Button>}
            />
        );
    return (
        <section className="address-form-page">
            <div className="section-card address-form-card">
                <Breadcrumb
                    routes={[{ name: "账号管理" }, { name: "我的地址", path: "/me/address" }, { name: pageName }]}
                />
                <div className="address-form-title">{pageName}</div>
                <Form
                    colon={false}
                    form={form}
                    initialValues={{ defaultFlag: false }}
                    labelAlign="right"
                >
                    <div className="address-form-grid">
                        <Form.Item
                            name="contacts"
                            label="联系人"
                            required
                            rules={[{ required: true, message: "请输入联系人" }]}
                        >
                            <Input maxLength={25} showCount placeholder="请输入联系人" />
                        </Form.Item>
                        <Form.Item
                            name="cellphone"
                            label="手机号"
                            required
                            rules={[
                                { required: true, message: "请输入手机号" },
                                { pattern: /^1[3456789]\d{9}$/, message: "请输入正确的手机号" }
                            ]}
                        >
                            <Input placeholder="请输入手机号" />
                        </Form.Item>
                        <Form.Item
                            name="region"
                            label="所在地区"
                            required
                            rules={[{ required: true, message: "请选择所在地区" }]}
                        >
                            <Cascader
                                options={areas}
                                fieldNames={{ label: "name", value: "id", children: "childs" }}
                                showSearch
                                placeholder="请选择所在地区"
                            />
                        </Form.Item>
                        <Form.Item
                            className="address-detail-field"
                            name="detailAddress"
                            label="详细地址"
                            required
                            rules={[{ required: true, message: "请输入详细地址" }]}
                        >
                            <Input maxLength={128} showCount placeholder="请输入详细地址" />
                        </Form.Item>
                        <Form.Item
                            name="zipCode"
                            label="邮政编码"
                            rules={[{ pattern: /^\d{6}$/, message: "请输入正确的邮编" }]}
                        >
                            <Input placeholder="请输入邮政编码" />
                        </Form.Item>
                        <Form.Item name="defaultFlag" label="设为默认地址" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </div>
                </Form>
            </div>
            <div className="address-form-footer">
                <Button type="primary" loading={saving} onClick={() => void requestSave()}>
                    提交
                </Button>
                <Button type="primary" onClick={() => back()}>
                    返回
                </Button>
            </div>
            <ZhMessageBox
                open={Boolean(pendingPayload)}
                tip="是否提交"
                onCancel={() => setPendingPayload(undefined)}
                onSubmit={save}
            />
        </section>
    );
}
